export type AssignmentPointsKind = 'general' | 'project' | 'routine';

export type AssignmentStatusValue =
  | 'pending'
  | 'confirmed'
  | 'delayed'
  | 'exempted'
  | 'excluded'
  | 'submitted';

export type AssignmentStudentStatus = {
  studentId: string;
  status: AssignmentStatusValue;
  earnedPoints: number;
  earnedCoins?: number;
  earnedItemIds?: string[];
  memo?: string;
  updatedAt?: string;
};

export type AssignmentCategory = {
  id: string;
  name: string;
  color: string;
};

/** 워크스페이스·클래스 과제 노트에 공유되는 커버 이미지(최대 20개) */
export type AssignmentCoverImage = {
  id: string;
  path: string;
  url: string;
  createdAt: string;
};

export const ASSIGNMENT_COVER_IMAGE_MAX = 20;

/** 과제 블럭 이미지 슬롯 비율 (정사각) */
export const ASSIGNMENT_COVER_ASPECT = 1;

export type AssignmentPointsSettings = {
  defaultPoints: number;
  statusDefaultPoints: {
    pending: number;
    confirmed: number;
    delayed: number;
    exempted: number;
  };
  categories: AssignmentCategory[];
};

export type AssignmentSubTask = {
  id: string;
  title: string;
  points: number;
  order: number;
};

export type GeneralAssignmentItem = {
  id: string;
  kind: 'general';
  title: string;
  dueDate: string; // YYYY-MM-DD
  basePoints: number;
  categoryId?: string;
  notes?: string;
  /** 과제 커버 이미지(노트 coverImages 라이브러리 id) */
  coverImageId?: string;
  /** 과제 완료 보상 */
  rewardEnabled?: boolean;
  rewardCoins?: number;
  rewardItemIds?: string[];
  assignedMemberIds: string[];
  studentStatuses: AssignmentStudentStatus[];
  createdAt: string;
  updatedAt: string;
};

export type ProjectAssignmentItem = {
  id: string;
  kind: 'project';
  title: string;
  dueDate: string;
  color: string;
  notes?: string;
  /** 과제 커버 이미지(노트 coverImages 라이브러리 id) */
  coverImageId?: string;
  rewardEnabled?: boolean;
  rewardCoins?: number;
  rewardItemIds?: string[];
  /** 보상 지급 완료 멤버 ID (수동 지급 버튼 기준) */
  rewardGrantedStudentIds?: string[];
  assignedMemberIds: string[];
  subTasks: AssignmentSubTask[];
  /** key: `${subTaskId}:${studentId}` */
  studentStatusesBySubTask: Record<string, AssignmentStudentStatus>;
  /** 챌린지 과제: 하위 과제 = 챌린지(assignment_challenge_items.id 와 동일) */
  challenge?: { projectId: string };
  createdAt: string;
  updatedAt: string;
};

export function isChallengeAssignment(
  item: { kind: string; challenge?: { projectId: string } } | null | undefined
): item is ProjectAssignmentItem & { challenge: { projectId: string } } {
  return item?.kind === 'project' && !!item.challenge?.projectId;
}

export type RoutineDateBucket = {
  date: string; // YYYY-MM-DD
  studentStatuses: AssignmentStudentStatus[];
  /** 루틴 요일 스케줄 밖 수동 추가 날짜 */
  isCustomDate?: boolean;
};

export type RoutineAssignmentItem = {
  id: string;
  kind: 'routine';
  title: string;
  startDate: string;
  dueDate: string;
  /** 0=일 … 6=토 */
  routineDays: number[];
  basePoints: number;
  categoryId?: string;
  notes?: string;
  /** 과제 커버 이미지(노트 coverImages 라이브러리 id) */
  coverImageId?: string;
  rewardEnabled?: boolean;
  rewardCoins?: number;
  rewardItemIds?: string[];
  /** 보상 지급에 필요한 완료 도달률(0–100). 기본 100 */
  rewardCompletionRate?: number;
  /** 보상 지급 완료 멤버 ID (수동 지급 버튼 기준) */
  rewardGrantedStudentIds?: string[];
  assignedMemberIds: string[];
  dateStatuses: RoutineDateBucket[];
  createdAt: string;
  updatedAt: string;
};

export type AssignmentPointsItem =
  | GeneralAssignmentItem
  | ProjectAssignmentItem
  | RoutineAssignmentItem;

export type AssignmentPointsNoteDoc = {
  workspaceId: string;
  gradeId: string;
  settings: AssignmentPointsSettings;
  items: AssignmentPointsItem[];
  /** 과제 커버 이미지 라이브러리(최대 ASSIGNMENT_COVER_IMAGE_MAX) */
  coverImages?: AssignmentCoverImage[];
  updatedAt: string;
};

export const DEFAULT_ASSIGNMENT_POINTS_SETTINGS: AssignmentPointsSettings = {
  defaultPoints: 10,
  /** confirmed: 포인트 ± 스텝(계산 비사용). delayed/exempted: 확인 포인트 대비 % */
  statusDefaultPoints: {
    pending: 0,
    confirmed: 1,
    delayed: 50,
    exempted: 0,
  },
  categories: [
    { id: 'cat-default', name: '기본', color: '#10504a' },
  ],
};

export function emptyAssignmentPointsNoteDoc(
  workspaceId: string,
  gradeId: string
): AssignmentPointsNoteDoc {
  return {
    workspaceId,
    gradeId,
    settings: { ...DEFAULT_ASSIGNMENT_POINTS_SETTINGS, categories: [...DEFAULT_ASSIGNMENT_POINTS_SETTINGS.categories] },
    items: [],
    coverImages: [],
    updatedAt: new Date().toISOString(),
  };
}
